'use client';

import Image from 'next/image';
import React from 'react';
import { FaCalendarAlt, FaClock, FaFacebookF, FaLinkedinIn, FaTwitter, FaUser, FaWhatsapp } from 'react-icons/fa';

import Breadcrumb from '@/components/Breadcrumb';
import Header from '@/components/Header';

import { useLanguage } from '@/contexts/LanguageContext';

export default function KisAylarindaMezoterapiler() {
  const { t } = useLanguage();
  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://drbariscin.com/tr/blog/kis-aylarinda-uygulanan-mezoterapiler-soguk-havada-cilt-yenilenmesi';
  const pageTitle = 'Kış Aylarında Uygulanan Mezoterapiler: Soğuk Havada Cilt Yenilenmesi';
  const pageDescription = 'Kış mevsiminde mezoterapi neden daha etkili olur? Soğuk havada cilt kuruluğunu ve matlığı gideren özel mezoterapi protokolleri.';

  const handleShare = (platform: string) => {
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(pageTitle + ' - ' + pageUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-gray-100">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Breadcrumb
            items={[
              { label: 'Ana Sayfa', href: '/tr' },
              { label: 'Blog', href: '/tr/blog' },
              { label: 'Kış Aylarında Mezoterapiler', href: '/tr/blog/kis-aylarinda-uygulanan-mezoterapiler-soguk-havada-cilt-yenilenmesi' },
            ]}
          />
        </div>

        {/* Article Header */}
        <article className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Featured Image */}
          <div className="relative h-64 sm:h-80">
            <Image
              src="/images/terleme-tedavisi.webp"
              alt="Kış aylarında mezoterapi uygulaması"
              fill
              className="object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <span className="bg-slate-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                Kış Bakımı
              </span>
            </div>
          </div>

          {/* Article Content */}
          <div className="p-6 sm:p-8">
            {/* Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Kış Aylarında Uygulanan Mezoterapiler: Soğuk Havada Cilt Yenilenmesi
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-6 mb-8 text-sm text-gray-600 border-b border-gray-200 pb-6">
              <div className="flex items-center">
                <FaUser className="w-4 h-4 mr-2" />
                <span>Op. Dr. Barış Çin</span>
              </div>
              <div className="flex items-center">
                <FaCalendarAlt className="w-4 h-4 mr-2" />
                <span>21 Şubat 2026</span>
              </div>
              <div className="flex items-center">
                <FaClock className="w-4 h-4 mr-2" />
                <span>6 dakika okuma</span>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="flex items-center gap-4 mb-8">
              <span className="text-gray-600 font-medium">Paylaş:</span>
              <div className="flex gap-3">
                <button className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors" onClick={() => handleShare('facebook')}>
                  <FaFacebookF className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-400 text-white rounded-full hover:bg-blue-500 transition-colors" onClick={() => handleShare('twitter')}>
                  <FaTwitter className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors" onClick={() => handleShare('linkedin')}>
                  <FaLinkedinIn className="w-4 h-4" />
                </button>
                <button className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors" onClick={() => handleShare('whatsapp')}>
                  <FaWhatsapp className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Soğuk hava, rüzgâr, düşük nem oranı ve kapalı ortamlardaki ısı farkı, cilt bariyerine zarar verir.
                Bu nedenle kış ayları, cildin en çok neme, vitamine ve onarıma ihtiyaç duyduğu dönemdir.
              </p>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Cilt bakımında birçok işlem yazın yapılamazken, mezoterapi kışın uygulanması en uygun medikal işlemlerden biridir.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1. Neden Kışın Mezoterapi?
              </h2>
              
              <div className="bg-slate-50 rounded-xl p-6 mb-6">
                <p className="text-slate-800 mb-4 font-medium">
                  Kışın:
                </p>
                <ul className="list-disc list-inside space-y-2 text-slate-800">
                  <li>Güneş ışığı zayıftır (pigment riski az).</li>
                  <li>Cilt daha az yağ üretir.</li>
                  <li>Soğuk hava nem kaybını hızlandırır.</li>
                </ul>
                <p className="text-slate-800 mt-4">
                  Bu faktörler, cildin hem nemini hem de elastikiyetini azaltır.
                  Mezoterapi bu dönemde derin nem desteği ve kolajen aktivasyonu sağlar.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                2. Kışa Özel Mezoterapi Protokolleri
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-slate-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Mezoterapi Türü</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">İçerik</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Etki</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Uygulama Aralığı</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Nemlendirme Mezoterapisi</td>
                      <td className="border border-gray-300 px-4 py-3">HA + Vitamin B5</td>
                      <td className="border border-gray-300 px-4 py-3">Kuruluğu azaltır</td>
                      <td className="border border-gray-300 px-4 py-3">2–3 hafta</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Anti-aging Mezoterapi</td>
                      <td className="border border-gray-300 px-4 py-3">Vitamin C + DMAE</td>
                      <td className="border border-gray-300 px-4 py-3">Cilt tonunu eşitler, sıkılaştırır</td>
                      <td className="border border-gray-300 px-4 py-3">3–4 hafta</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Kış Parlaklık Mezoterapisi</td>
                      <td className="border border-gray-300 px-4 py-3">Glutatyon + Aminoasit</td>
                      <td className="border border-gray-300 px-4 py-3">Cildi aydınlatır, matlığı giderir</td>
                      <td className="border border-gray-300 px-4 py-3">2–3 hafta</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Kolajen Stimülasyonu</td>
                      <td className="border border-gray-300 px-4 py-3">Nucleofill / Sculptra</td>
                      <td className="border border-gray-300 px-4 py-3">Elastin sentezini artırır</td>
                      <td className="border border-gray-300 px-4 py-3">6–8 hafta</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                3. Uygulama Süreci
              </h2>
              
              <div className="bg-blue-50 rounded-xl p-6 mb-6">
                <ol className="list-decimal list-inside space-y-3 text-blue-800">
                  <li>Cilt temizlenir ve lokal anestezik krem uygulanır.</li>
                  <li>Mikro iğnelerle cilt altına özel kış kokteyli enjekte edilir.</li>
                  <li>Ardından yatıştırıcı maske ve LED ışık terapisiyle hücre yenilenmesi desteklenir.</li>
                </ol>
                <p className="text-blue-800 mt-4 font-medium">
                  Seans süresi: yaklaşık 40 dakika.
                </p>
                <p className="text-blue-800">
                  Kızarıklık 1–2 saat içinde geçer, kişi günlük yaşamına dönebilir.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                4. Kış Mezoterapisinin Faydaları
              </h2>
              
              <div className="bg-green-50 rounded-xl p-6 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <span className="text-green-600 mr-2">✅</span>
                      <span className="text-green-800">Kuruluk ve pul pul dökülmeyi önler</span>
                    </div>
                    <div className="flex items-center">
                      <span className="text-green-600 mr-2">✅</span>
                      <span className="text-green-800">Ciltteki donukluk ve matlığı giderir</span>
                    </div>
                    <div className="flex items-center">
                      <span className="text-green-600 mr-2">✅</span>
                      <span className="text-green-800">Nem bariyerini güçlendirir</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <span className="text-green-600 mr-2">✅</span>
                      <span className="text-green-800">İnce kırışıklıkları azaltır</span>
                    </div>
                    <div className="flex items-center">
                      <span className="text-green-600 mr-2">✅</span>
                      <span className="text-green-800">Kolajen üretimini uyarır</span>
                    </div>
                  </div>
                </div>
                <div className="bg-green-100 rounded-lg p-4 mt-4">
                  <p className="text-green-900 font-medium">
                    💬 Kışın yapılan mezoterapi, yazın yapılan cilt bakımından iki kat daha kalıcıdır.
                  </p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                5. Kimler İçin Uygun?
              </h2>
              
              <div className="bg-purple-50 rounded-xl p-6 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <span className="text-purple-600 mr-2">✅</span>
                      <span className="text-purple-800">Kuru veya karma cilt yapısına sahip kişiler</span>
                    </div>
                    <div className="flex items-center">
                      <span className="text-purple-600 mr-2">✅</span>
                      <span className="text-purple-800">Sigara içen, mat ciltli bireyler</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <span className="text-purple-600 mr-2">✅</span>
                      <span className="text-purple-800">Güneş sonrası cilt yorgunluğu yaşayanlar</span>
                    </div>
                    <div className="flex items-center">
                      <span className="text-purple-600 mr-2">✅</span>
                      <span className="text-purple-800">25 yaş üzeri herkes</span>
                    </div>
                  </div>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                6. Dikkat Edilmesi Gerekenler
              </h2>
              
              <div className="bg-orange-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside space-y-2 text-orange-800">
                  <li>İşlem sonrası 24 saat sauna ve sıcak duştan kaçınılmalı</li>
                  <li>48 saat makyaj yapılmamalı</li>
                  <li>İlk hafta dudak masajı yapılmamalıdır</li>
                  <li>Bol su içilmeli, cilt nemi desteklenmeli</li>
                  <li>SPF 30 ve üzeri koruyucu kullanılmalı</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                7. Seans Planı
              </h2>
              
              <div className="bg-indigo-50 rounded-xl p-6 mb-6">
                <div className="flex items-center mb-4">
                  <span className="text-indigo-600 text-2xl mr-3">📅</span>
                  <span className="text-indigo-800 font-medium">3 ana seans (ilk ay içinde)</span>
                </div>
                <div className="flex items-center mb-4">
                  <span className="text-indigo-600 text-2xl mr-3">📅</span>
                  <span className="text-indigo-800 font-medium">2 destek seansı (her 2 ayda bir)</span>
                </div>
                <p className="text-indigo-800">
                  Sonrasında yılda 2 kez koruma kürü önerilir.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Sık Sorulan Sorular (FAQ)
              </h2>
              
              <div className="space-y-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">1. Kışın mezoterapi yaptırmak zararlı mı?</h3>
                  <p className="text-gray-600">Hayır, tam tersine cilt onarımı için en ideal dönemdir.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">2. Soğuk havada cilt daha mı geç iyileşir?</h3>
                  <p className="text-gray-600">Hayır, mezoterapi sonrası kızarıklık yaz dönemine göre daha hızlı geçer.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">3. Kış mezoterapisi kimlere önerilmez?</h3>
                  <p className="text-gray-600">Soğuk alerjisi, egzama veya aktif enfeksiyonu olan kişilere yapılmaz.</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Kaynakça
              </h2>
              
              <div className="bg-gray-50 rounded-xl p-6 mb-8">
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                  <li>ISAPS Global Report (2024) – "Seasonal Optimization of Mesotherapy Treatments"</li>
                  <li>Türk Estetik Tıp Derneği (TETD) – "Kış Dönemi Cilt Mezoterapileri"</li>
                  <li>Harvard Health (2023) – "Cold Weather and Skin Barrier Research"</li>
                  <li>Mayo Clinic (2024) – "Dermal Hydration and Mesotherapy Protocols"</li>
                </ul>
              </div>

              <div className="flex flex-wrap gap-2 mb-8">
                <span className="bg-slate-100 text-slate-800 px-3 py-1 rounded-full text-sm">#mezoterapi</span>
                <span className="bg-slate-100 text-slate-800 px-3 py-1 rounded-full text-sm">#kışbakımı</span>
                <span className="bg-slate-100 text-slate-800 px-3 py-1 rounded-full text-sm">#ciltkuruluğu</span>
                <span className="bg-slate-100 text-slate-800 px-3 py-1 rounded-full text-sm">#nem</span>
                <span className="bg-slate-100 text-slate-800 px-3 py-1 rounded-full text-sm">#vitamin</span>
                <span className="bg-slate-100 text-slate-800 px-3 py-1 rounded-full text-sm">#hyaluronikasit</span>
              </div>

              {/* Social Share Buttons */}
              <div className="border-t pt-8 mt-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Bu yazıyı paylaş:</h3>
                <div className="flex space-x-4">
                  <button
                    onClick={() => handleShare('facebook')}
                    className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <FaFacebookF className="w-4 h-4" />
                    <span>Facebook</span>
                  </button>
                  <button
                    onClick={() => handleShare('twitter')}
                    className="flex items-center space-x-2 bg-blue-400 text-white px-4 py-2 rounded-lg hover:bg-blue-500 transition-colors"
                  >
                    <FaTwitter className="w-4 h-4" />
                    <span>Twitter</span>
                  </button>
                  <button
                    onClick={() => handleShare('linkedin')}
                    className="flex items-center space-x-2 bg-blue-700 text-white px-4 py-2 rounded-lg hover:bg-blue-800 transition-colors"
                  >
                    <FaLinkedinIn className="w-4 h-4" />
                    <span>LinkedIn</span>
                  </button>
                  <button
                    onClick={() => handleShare('whatsapp')}
                    className="flex items-center space-x-2 bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors"
                  >
                    <FaWhatsapp className="w-4 h-4" />
                    <span>WhatsApp</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </article>

        {/* CTA Section */}
        <div className="mt-12 bg-gradient-to-r from-slate-600 to-gray-700 rounded-3xl p-8 text-white text-center">
          <h3 className="text-2xl font-bold mb-4">Kış Mezoterapisi Danışmanlığı</h3>
          <p className="mb-6">Uzman doktorumuzdan kış aylarında mezoterapi tedavisi hakkında detaylı bilgi alın.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`https://wa.me/905438999586?text=${encodeURIComponent('Kış mezoterapisi hakkında bilgi almak istiyorum.')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-6 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
            >
              <FaWhatsapp className="w-5 h-5 mr-2" />
              WhatsApp ile İletişim
            </a>
            <a
              href="/tr/iletisim"
              className="inline-flex items-center justify-center px-6 py-3 bg-white text-slate-600 rounded-full hover:bg-gray-100 transition-colors"
            >
              Randevu Al
            </a>
          </div>
        </div>

        {/* Related Articles */}
        <div className="mt-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">İlgili Yazılar</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <a
              href="/tr/blog/cilt-kurulugunda-mezoterapi-derin-nem-destegi-ve-hucresel-yenilenme"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Cilt Kuruluğunda Mezoterapi</h4>
              <p className="text-gray-600 text-sm">Cilt kuruluğuna karşı mezoterapi nasıl etki eder? Derin nem desteği ve hücresel yenilenme.</p>
            </a>
            <a
              href="/tr/blog/mezoterapi-etkileri-ne-zaman-gorulur"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Mezoterapinin Etkileri Ne Zaman Görülür?</h4>
              <p className="text-gray-600 text-sm">Mezoterapi uygulamasından sonra etkilerin ne zaman görülmeye başladığı hakkında bilgiler.</p>
            </a>
            <a
              href="/tr/blog/botoks-nedir-nasil-yapilir"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Botoks Nedir, Nasıl Yapılır?</h4>
              <p className="text-gray-600 text-sm">Botoksun ne olduğu, nasıl çalıştığı ve uygulama süreci hakkında detaylı bilgiler.</p>
            </a>
            <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
              <p className="text-gray-600 mb-4">
                Daha fazla bilgilendirici içerik için bizi takip edin.
              </p>
              <a
                href="/tr/blog"
                className="inline-flex items-center px-4 py-2 bg-slate-600 text-white rounded-full hover:bg-slate-700 transition-colors text-sm"
              >
                Tüm Blog Yazılarını Görüntüle
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
